import ee from '../../services/earth-engine';
import { eeEval } from '../utils/index.js';
import { addTideBandToImage } from './tideBands';
import { PADDING_DAYS, ONE_DAY_MS } from './constants.js';

/**
 * @function hasTideSeries
 * @description Whether this analysis has both a gauge series and the correction switched on.
 * @param {Object} [fileData] - The uploaded files' state.
 * @param {Object} [data] - The analysis configuration.
 * @memberof module:Bathymetry
 * @returns {Boolean} True when the tide correction can run.
 */
export const hasTideSeries = (fileData, data) => fileData?.tideData?.length > 0 && !!data?.tideConfig?.enabled;

/**
 * @function tideEntryTime
 * @description When a gauge entry was measured, in milliseconds. Entries arrive either as a
 * `[date, level]` pair or as an object naming the date one of four ways, depending on the file.
 * @param {Array|Object} entry - One gauge reading.
 * @memberof module:Bathymetry
 * @returns {Number} The timestamp.
 */
export const tideEntryTime = (entry) => {
  if (Array.isArray(entry)) return new Date(entry[0]).getTime();
  return new Date(entry.date ?? entry.datetime ?? entry.time ?? entry.timestamp).getTime();
};

/**
 * @function sliceTideData
 * @description Narrows the gauge series to a few days either side of the dates being analysed, so
 * a multi-year file is not carried into Earth Engine whole for a single acquisition.
 * @param {Array} [tideData] - The full series.
 * @param {Array<String>} [dates] - The acquisition dates to keep around.
 * @memberof module:Bathymetry
 * @returns {Array} The narrowed series, or the series unchanged when there is nothing to narrow by.
 */
export const sliceTideData = (tideData, dates) => {
  if (!tideData?.length || !dates?.length) return tideData;
  const windows = dates.filter(Boolean).map((d) => {
    const t = new Date(d).getTime();
    return [t - PADDING_DAYS * ONE_DAY_MS, t + PADDING_DAYS * ONE_DAY_MS];
  });
  if (!windows.length) return tideData;
  return tideData.filter((entry) => {
    const t = tideEntryTime(entry);
    return windows.some(([min, max]) => t >= min && t <= max);
  });
};

/**
 * @function normalisedTideSeries
 * @description The gauge series in the one shape the chart reads, whichever of the accepted
 * spellings the file used for the date and the level.
 * @param {Array} [tideData] - The full series.
 * @memberof module:Bathymetry
 * @returns {Array<Object>} `{date, level}` entries.
 */
export const normalisedTideSeries = (tideData) =>
  (tideData || []).map((entry) => {
    if (Array.isArray(entry)) return { date: new Date(entry[0]).toISOString(), level: entry[1] };
    const date = entry.date ?? entry.datetime ?? entry.time ?? entry.timestamp;
    const level = entry.level ?? entry.waterLevel ?? entry.tideLevel ?? entry.value;
    return { date: new Date(date).toISOString(), level: parseFloat(level) };
  });

/**
 * @function firstImageDate
 * @description The first date of an acquisition label, which is either one date or a range
 * written as `start/end`.
 * @param {String} [imageDate] - The label.
 * @memberof module:Bathymetry
 * @returns {String|null} The date, or null when there is none.
 */
export const firstImageDate = (imageDate) =>
  imageDate ? (imageDate.includes('/') ? imageDate.split('/')[0] : imageDate) : null;

/**
 * @function analysedImageDates
 * @description Every acquisition date the result covers: the composite's list when there is one,
 * the single date otherwise.
 * @param {Object} [data] - The analysis configuration.
 * @memberof module:Bathymetry
 * @returns {Array<String>} The dates, empty when none is known.
 */
export const analysedImageDates = (data) => {
  if (data?.selectedImageDates?.length > 0) return data.selectedImageDates;
  const single = firstImageDate(data?.imageDate);
  return single ? [single] : [];
};

/**
 * @function probeTidePoints
 * @description The gauge level at each acquisition, read the way the correction reads it: a bare
 * image stamped with the date is run through {@link addTideBandToImage}, so the chart marks the
 * level the model actually subtracted rather than a separately interpolated one.
 * A failure here costs the chart its markers and nothing else, so it is logged and swallowed.
 * @param {Object} [fileData] - The uploaded files' state.
 * @param {Object} [data] - The analysis configuration.
 * @memberof module:Bathymetry
 * @returns {Promise<Object>} `{dates, points}` - the dates probed, and the `{date, tideLevel}`
 *  entries that came back.
 */
export const probeTidePoints = async (fileData, data) => {
  const dates = analysedImageDates(data);
  if (!dates.length) return { dates: [], points: [] };
  try {
    const levels = await eeEval(
      ee.Dictionary.fromLists(
        dates,
        dates.map((dateStr) => {
          const probe = ee.Image(1).set('system:time_start', ee.Date(dateStr).millis());
          const window = sliceTideData(fileData.tideData, [dateStr]);
          return addTideBandToImage(probe, window, fileData.timezoneOffset ?? 0, probe, data.tideConfig).get(
            'tideValue',
          );
        }),
      ),
    );
    return {
      dates,
      points: dates
        .map((dateStr) => (levels[dateStr] != null ? { date: dateStr, tideLevel: parseFloat(levels[dateStr]) } : null))
        .filter(Boolean),
    };
  } catch (e) {
    console.warn('[probeTidePoints] tide probes failed', e);
    return { dates: [], points: [] };
  }
};

/**
 * @function mergeTidePoints
 * @description Folds freshly probed levels into the markers already on the chart, replacing every
 * date that was probed - including one that came back empty, which should drop its stale marker
 * rather than keep it.
 * @param {Array<Object>} [existing] - The markers already held.
 * @param {Object} probed - `{dates, points}`, as {@link probeTidePoints} returns them.
 * @memberof module:Bathymetry
 * @returns {Array<Object>} The markers, in date order.
 */
export const mergeTidePoints = (existing, { dates, points }) =>
  [...(existing || []).filter((p) => !dates.includes(p.date)), ...points].sort((a, b) => a.date.localeCompare(b.date));

/**
 * @function applyTideToDepth
 * @description Attaches the gauge level to the modelled depth and subtracts it, keeping the
 * uncorrected surface alongside as `pred_depth_raw`. The subtraction is guarded on the band
 * actually being there, since the gauge may hold nothing within the window of an acquisition.
 * @param {Object} image - The modelled depth, as an `ee.Image` carrying `pred_depth`.
 * @param {Object} params
 * @param {Object} params.data - The analysis configuration.
 * @param {Object} params.fileData - The uploaded files' state.
 * @param {Object} params.fallbackTimestamp - The image to read the time from when the analysis
 *  carries no acquisition date.
 * @memberof module:Bathymetry
 * @returns {Object} The corrected `ee.Image`.
 */
export const applyTideToDepth = (image, { data, fileData, fallbackTimestamp }) => {
  const targetDate = firstImageDate(data.imageDate);
  const timestamp = targetDate ? ee.Image(1).set('system:time_start', ee.Date(targetDate).millis()) : fallbackTimestamp;
  const tided = addTideBandToImage(
    image,
    sliceTideData(fileData.tideData, targetDate ? [targetDate] : []),
    fileData.timezoneOffset ?? 0,
    timestamp,
    data.tideConfig,
  );
  const withRaw = tided.addBands(tided.select('pred_depth').rename('pred_depth_raw'), null, true);
  if (!hasTideSeries(fileData, data)) return withRaw;
  const corrected = ee.Image(
    ee.Algorithms.If(
      withRaw.bandNames().contains('tideLevel'),
      withRaw.select('pred_depth').subtract(withRaw.select('tideLevel')).rename('pred_depth'),
      withRaw.select('pred_depth'),
    ),
  );
  return withRaw.addBands(corrected, null, true);
};
